import Badge from '../shared/Badge';

export default function QuestionItem({ question, isSelected, onClick, onAnswerClick }) {
  const handleAnswerClick = (e) => {
    e.stopPropagation(); // Prevent triggering the question click
    if (onAnswerClick) {
      onAnswerClick(question);
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const messageCount = question.discussion_messages?.length || 0;
  const displayProduct = question.product || 'None';
  const displayCategory = question.category || 'None';
  const timestamp = question.updated_at || question.created_at;

  return (
    <div
      onClick={onClick}
      className={`
        p-3 rounded-lg cursor-pointer transition-all duration-200
        ${isSelected
          ? 'bg-purple-600/20 border-2 border-purple-500'
          : 'bg-gray-800/50 border-2 border-gray-700 hover:bg-gray-800 hover:border-gray-600'
        }
      `}
    >
      {/* Question Title and Answer Button */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="text-sm font-medium text-white flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
          {question.title}
        </h3>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Answer Button */}
          <button
            onClick={handleAnswerClick}
            className="p-1.5 rounded-md bg-green-600/20 hover:bg-green-600/30 border border-green-500/30 hover:border-green-500/50 text-green-400 hover:text-green-300 transition-all duration-200"
            title="Answer Question"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>

          {/* Answered Badge */}
          {question.is_answered && (
            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-500/20 text-green-400 border border-green-500/30">
              Answered
            </span>
          )}
        </div>
      </div>

      {/* Pills for Product, Category, and Messages */}
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-1.5">
          <Badge
            variant={question.product ? 'blue' : 'gray'}
                     >
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
            </svg>
            {displayProduct}
          </Badge>

          <Badge
            variant={question.category ? 'purple' : 'gray'}
                     >
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
            </svg>
            {displayCategory}
          </Badge>

          <Badge variant="gray">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
            </svg>
            {messageCount}
          </Badge>
        </div>

        {timestamp && (
          <span className="text-xs text-gray-500">{formatTimestamp(timestamp)}</span>
        )}
      </div>
    </div>
  );
}
